import { parentDirOfMarkdown } from './markdownPaths';

/** Repo folder for images belonging to `database/foo/bar.md` → `database/foo/bar/_images/ */
export function imagesDirForMarkdown(mdPath: string): string {
  return mdPath.replace(/\.md$/i, '/_images');
}

export function pageSlugFromMarkdown(mdPath: string): string {
  const base = mdPath.split('/').pop() ?? mdPath;
  return base.replace(/\.md$/i, '');
}

/** `garden-notes` → `database/parent/garden-notes/_images`, plus `-`/`_` folder variants. */
export function imageDirsForPageSlug(parentDir: string, slug: string): string[] {
  const variants = new Set<string>([slug]);
  const underscored = slug.replace(/-/g, '_');
  const hyphenated = slug.replace(/_/g, '-');
  variants.add(underscored);
  variants.add(hyphenated);
  return [...variants].map(s => `${parentDir}/${s}/_images`);
}

export function imageRepoPath(mdPath: string, filename: string): string {
  return `${imagesDirForMarkdown(mdPath)}/${filename}`;
}

/** Paths to try when loading (canonical folder, slug spelling variants, sibling legacy folders). */
export function alternateImageRepoPaths(repoPath: string, mdPath?: string): string[] {
  const paths: string[] = [repoPath];
  if (/\/_video\//i.test(repoPath)) return paths;
  const m = /^(database\/[^/]+)(\/_images\/.+)$/i.exec(repoPath);
  if (m) {
    const dir = m[1]!;
    const rest = m[2]!;
    const underscored = dir.replace(/-/g, '_');
    const hyphenated = dir.replace(/_/g, '-');
    if (underscored !== dir) paths.push(`${underscored}${rest}`);
    if (hyphenated !== dir) paths.push(`${hyphenated}${rest}`);
  }

  if (mdPath) {
    const filename = repoPath.split('/').pop();
    if (filename) {
      const parent = parentDirOfMarkdown(mdPath);
      const slug = pageSlugFromMarkdown(mdPath);
      for (const dir of imageDirsForPageSlug(parent, slug)) {
        paths.push(`${dir}/${filename}`);
      }
    }
  }

  return [...new Set(paths)];
}

/** Other page image folders under the same parent (e.g. legacy folder left after a rename). */
export async function legacySiblingImagePaths(
  github: { listBlobPathsUnderPrefix(prefix: string): Promise<string[]> },
  mdPath: string,
  filename: string
): Promise<string[]> {
  const parent = parentDirOfMarkdown(mdPath);
  const canonicalPrefix = `${imagesDirForMarkdown(mdPath)}/`;
  const out: string[] = [];
  try {
    for (const p of await github.listBlobPathsUnderPrefix(`${parent}/`)) {
      if (!p.endsWith(`/_images/${filename}`)) continue;
      if (p.startsWith(canonicalPrefix)) continue;
      out.push(p);
    }
  } catch {
    /* best-effort */
  }
  return out;
}


const ALLOWED_EXT = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif']);

function randomImageId(): string {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID().replace(/-/g, '').slice(0, 12)
    : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

/** Filename for CI / scripts (same shape as browser uploads). */
export function newImageFilenameForExt(ext = 'jpg'): string {
  let e = ext.toLowerCase();
  if (e === 'jpeg') e = 'jpg';
  if (!ALLOWED_EXT.has(e)) e = 'jpg';
  return `${randomImageId()}.${e}`;
}

export function newImageFilename(file: File): string {
  const fromName = file.name.split('.').pop()?.toLowerCase() ?? '';
  let ext = ALLOWED_EXT.has(fromName) ? fromName : '';
  if (!ext && file.type) {
    const m = /^image\/(jpeg|png|gif|webp|heic|heif)/i.exec(file.type);
    if (m) ext = m[1]!.toLowerCase();
  }
  if (!ext) ext = 'jpg';
  if (ext === 'jpeg') ext = 'jpg';
  return `${randomImageId()}.${ext}`;
}

export function mimeForImagePath(path: string): string {
  const ext = path.split('.').pop()?.toLowerCase() ?? '';
  switch (ext) {
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg';
    case 'png':
      return 'image/png';
    case 'gif':
      return 'image/gif';
    case 'webp':
      return 'image/webp';
    case 'heic':
    case 'heif':
      return 'image/heic';
    default:
      return 'application/octet-stream';
  }
}
